import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useEffect } from "react";
import { X, Save } from "lucide-react";
import SelectBuscable from '@/shared/components/SelectBuscable';
import { apiService } from '@/services/core/api.service';
import type { UserProfile } from '@/services/api';
import toast from "react-hot-toast";
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

// ─── Shared input styles ──────────────────────────────────────────────────────

const INPUT =
  "w-full px-3 py-2 border-0 rounded-md bg-[#F6F9FB] text-sm text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 transition-colors";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AlmacenFormData {
  nombre: string;
  responsableUserId: string;
  descripcion: string;
}

interface AlmacenFormModalProps {
  mode: "create" | "edit";
  initial?: Partial<AlmacenFormData>;
  onClose: () => void;
  onSubmit: (data: AlmacenFormData) => Promise<void>;
}

const EMPTY: AlmacenFormData = { nombre: "", responsableUserId: "", descripcion: "" };

// ─── Component ────────────────────────────────────────────────────────────────

const AlmacenFormModal = ({
  mode,
  initial = {},
  onClose,
  onSubmit,
}: AlmacenFormModalProps) => {
  const [form, setForm]     = useState<AlmacenFormData>({ ...EMPTY, ...initial });
  const [users, setUsers]   = useState<UserProfile[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiService.request<UserProfile[]>("/users/public/all").then(setUsers).catch(() => {});
  }, []);

  const set =
    (field: keyof AlmacenFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const isValid = (form.nombre ?? "").trim() !== "";

  const handleSubmit = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    try {
      await onSubmit(form);
      onClose();
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      const e = err as { statusCode?: number; message?: string | string[] };
      const msg = e.statusCode === 500
        ? "Error del servidor. Inténtalo de nuevo."
        : Array.isArray(e.message) ? e.message.join(" ") : (e.message ?? "Error al guardar");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const title = mode === "create" ? "Registrar nuevo almacén" : "Editar almacén";

  return (
    <CapaDeModal>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl mx-4">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="h-6 w-6 bg-[#D9D9D9] rounded-full flex items-center justify-center hover:bg-[#D9D9D9]/60 transition-colors"
          >
            <X className="w-3.5 h-3.5 text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-4 space-y-4">

          {/* Nombre + Responsable */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.nombre}
                onChange={set("nombre")}
                placeholder="Ej. Almacén 1"
                className={INPUT}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Responsable
              </label>
              <SelectBuscable
                variante="formulario"
                opciones={users.map((u) => ({
                  value: String(u.id),
                  label: `${u.firstName} ${u.lastName}`,
                }))}
                value={form.responsableUserId}
                onChange={(v) => setForm((prev) => ({ ...prev, responsableUserId: v }))}
                placeholder="Seleccionar"
                placeholderDeBusqueda="Buscar responsable..."
              />
            </div>
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              value={form.descripcion}
              onChange={set("descripcion")}
              rows={4}
              placeholder="Descripción corta del almacén"
              className={`${INPUT} resize-none`}
            />
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={estiloDeBoton("secundario")}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || saving}
            className={estiloDeBoton("primario")}
          >
            <Save className="w-4 h-4" />
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </CapaDeModal>
  );
};

export default AlmacenFormModal;
